/**
 * AgentCodeEditor.jsx
 *
 * Shared Monaco-based Python code editor component for writing and submitting
 * AI agent code directly in the browser.
 *
 * Props:
 *   template       {string}   - Default boilerplate code shown on first load
 *   onSubmit       {Function} - Called with the full injected code string
 *   loading        {bool}     - Shows spinner/disabled state while simulation runs
 *   theme          {object}   - THEME colour tokens from the parent game component
 *   header         {string}   - (Optional) Python header code to prepend
 *   footer         {string}   - (Optional) Python footer code to append
 */
import React, { useRef, useState, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Chip,
    Collapse,
    Alert,
    Tooltip,
    IconButton,
} from '@mui/material';
import {
    PlayArrow,
    RestartAlt,
    CheckCircleOutline,
    ErrorOutline,
    InfoOutlined,
} from '@mui/icons-material';
import { COLOR } from '../ui/tokens';

// ---------------------------------------------------------------------------
// Lightweight client-side Python syntax check
// Catches the most common errors without requiring a Python interpreter.
// ---------------------------------------------------------------------------
function checkPythonSyntax(code) {
    const errors = [];

    // 1. Bracket / paren / brace balance
    const pairs = { '(': ')', '[': ']', '{': '}' };
    const stack = [];
    let inSingleStr = false;
    let inDoubleStr = false;
    let inTripleSingle = false;
    let inTripleDouble = false;
    const lines = code.split('\n');

    for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        for (let ci = 0; ci < line.length; ci++) {
            const ch = line[ci];
            const prev = ci > 0 ? line[ci - 1] : '';
            const peek3 = line.slice(ci, ci + 3);

            // Skip escaped chars
            if (prev === '\\') continue;

            // Triple-quoted strings
            if (!inSingleStr && !inDoubleStr) {
                if (!inTripleSingle && !inTripleDouble && peek3 === "'''") { inTripleSingle = true; ci += 2; continue; }
                if (inTripleSingle && peek3 === "'''") { inTripleSingle = false; ci += 2; continue; }
                if (!inTripleSingle && !inTripleDouble && peek3 === '"""') { inTripleDouble = true; ci += 2; continue; }
                if (inTripleDouble && peek3 === '"""') { inTripleDouble = false; ci += 2; continue; }
            }
            if (inTripleSingle || inTripleDouble) continue;

            // Single-line string toggling
            if (ch === "'" && !inDoubleStr) { inSingleStr = !inSingleStr; continue; }
            if (ch === '"' && !inSingleStr) { inDoubleStr = !inDoubleStr; continue; }
            if (inSingleStr || inDoubleStr) continue;

            // Comment - rest of line is irrelevant
            if (ch === '#') break;

            if (pairs[ch]) { stack.push({ ch, line: li + 1 }); }
            else if (Object.values(pairs).includes(ch)) {
                if (stack.length === 0 || pairs[stack[stack.length - 1].ch] !== ch) {
                    errors.push(`Line ${li + 1}: Unexpected '${ch}'`);
                } else {
                    stack.pop();
                }
            }
        }
    }
    stack.forEach(s => errors.push(`Line ${s.line}: Unclosed '${s.ch}'`));

    // 2. Indentation: tabs mixed with spaces on same line
    lines.forEach((line, i) => {
        if (/^\t+ /.test(line) || /^ +\t/.test(line)) {
            errors.push(`Line ${i + 1}: Mixed tabs and spaces`);
        }
    });

    // 3. def/class must end with ':'
    lines.forEach((line, i) => {
        const trimmed = line.trim();
        if (/^(def |class |if |elif |else|for |while |with |try:|except|finally)/.test(trimmed)) {
            if (!trimmed.endsWith(':') && !trimmed.endsWith('\\') && !trimmed.endsWith(',')) {
                // Multi-line defs are fine; only flag single-line obviously broken ones
                if (/^(def |class )\w+/.test(trimmed) && !/[:(]/.test(trimmed)) {
                    errors.push(`Line ${i + 1}: Missing colon after def/class`);
                }
            }
        }
    });

    return errors;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AgentCodeEditor = ({ template, onSubmit, loading = false, theme, header = '', footer = '' }) => {
    const THEME = theme || {
        bg: COLOR.bg,
        cardBg: COLOR.surface,
        border: COLOR.border,
        primary: COLOR.primary,
        secondary: COLOR.textMuted,
        accent: COLOR.danger,
    };

    const editorRef = useRef(null);
    const [syntaxErrors, setSyntaxErrors] = useState([]);
    const [syntaxOk, setSyntaxOk] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [hasEdited, setHasEdited] = useState(false);

    const handleEditorDidMount = useCallback((editor) => {
        editorRef.current = editor;
    }, []);

    const getCode = useCallback(() => {
        return editorRef.current ? editorRef.current.getValue() : template;
    }, [template]);

    const handleReset = useCallback(() => {
        if (editorRef.current) {
            editorRef.current.setValue(template);
            setSyntaxErrors([]);
            setSyntaxOk(false);
            setHasEdited(false);
        }
    }, [template]);

    const handleChange = useCallback(() => {
        setHasEdited(true);
        setSyntaxOk(false);
        setSyntaxErrors([]);
    }, []);

    const handleValidate = useCallback(() => {
        const code = getCode();
        const errors = checkPythonSyntax(code);
        setSyntaxErrors(errors);
        setSyntaxOk(errors.length === 0);
        setShowErrors(errors.length > 0);
        return errors.length === 0;
    }, [getCode]);

    const handleSubmit = useCallback(() => {
        const code = getCode();
        const ok = checkPythonSyntax(code).length === 0;
        if (!ok) {
            const errors = checkPythonSyntax(code);
            setSyntaxErrors(errors);
            setSyntaxOk(false);
            setShowErrors(true);
            return;
        }
        setSyntaxErrors([]);
        setSyntaxOk(true);

        // Inject header and footer around user code
        const fullCode = header + '\n' + code + '\n' + footer;
        onSubmit(fullCode);
    }, [getCode, onSubmit, header, footer]);

    return (
        <Box>
            {/* Editor header toolbar */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                px: 0.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{
                        color: THEME.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: '0.65rem',
                    }}>
                        agent.py
                    </Typography>
                    {hasEdited && (
                        <Chip
                            label="edited"
                            size="small"
                            sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(255,200,0,0.15)', color: '#fbbf24', border: '1px solid #fbbf24' }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Check syntax">
                        <IconButton
                            size="small"
                            onClick={handleValidate}
                            sx={{ color: syntaxOk ? THEME.primary : THEME.secondary }}
                        >
                            <CheckCircleOutline fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset to template">
                        <IconButton size="small" onClick={handleReset} sx={{ color: THEME.secondary }}>
                            <RestartAlt fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Monaco editor */}
            <Box sx={{
                border: `1px solid ${syntaxErrors.length > 0 ? THEME.accent : (syntaxOk ? THEME.primary : THEME.border)}`,
                borderRadius: 2,
                overflow: 'hidden',
                mb: 1.5,
            }}>
                <Editor
                    height="320px"
                    defaultLanguage="python"
                    defaultValue={template}
                    theme="vs-dark"
                    onChange={handleChange}
                    onMount={handleEditorDidMount}
                    options={{
                        fontSize: 12,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        lineNumbers: 'on',
                        renderLineHighlight: 'all',
                        tabSize: 4,
                        insertSpaces: true,
                        automaticLayout: true,
                        padding: { top: 8, bottom: 8 },
                    }}
                />
            </Box>

            {/* Syntax feedback */}
            <Collapse in={showErrors && syntaxErrors.length > 0}>
                <Alert
                    severity="error"
                    icon={<ErrorOutline />}
                    onClose={() => setShowErrors(false)}
                    sx={{ mb: 1.5, bgcolor: 'rgba(239,71,67,0.1)', color: COLOR.danger, border: '1px solid rgba(239,71,67,0.3)', '& .MuiAlert-icon': { color: COLOR.danger } }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                        Syntax errors found:
                    </Typography>
                    {syntaxErrors.slice(0, 5).map((e, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                            {e}
                        </Typography>
                    ))}
                    {syntaxErrors.length > 5 && (
                        <Typography variant="caption" sx={{ color: COLOR.danger }}>
                            ...and {syntaxErrors.length - 5} more
                        </Typography>
                    )}
                </Alert>
            </Collapse>

            {syntaxOk && !showErrors && (
                <Alert
                    severity="success"
                    icon={<CheckCircleOutline />}
                    sx={{ mb: 1.5, bgcolor: 'rgba(44,187,93,0.1)', color: THEME.primary, border: `1px solid rgba(44,187,93,0.3)`, '& .MuiAlert-icon': { color: THEME.primary } }}
                >
                    <Typography variant="caption">Syntax OK</Typography>
                </Alert>
            )}

            {/* Hint */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
                <InfoOutlined sx={{ fontSize: 14, color: THEME.secondary, mt: 0.2, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: THEME.secondary, lineHeight: 1.4 }}>
                    Edit the highlighted function to implement your strategy.
                    Headers and game loop are automatically injected at submission.
                </Typography>
            </Box>

            {/* Submit button */}
            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                sx={{
                    bgcolor: '#2563eb',
                    color: 'white',
                    '&:disabled': { bgcolor: '#1e293b', color: '#64748b' },
                }}
            >
                {loading ? 'Running Agent...' : 'Run Agent'}
            </Button>
        </Box>
    );
};

export default AgentCodeEditor;
